/**
 * How a story is built, kept out of `stories.ts` so a per-file story can call it.
 *
 * WHY THIS FILE EXISTS — and it is not tidiness. `stories.ts` globs
 * `./stories/*.ts` eagerly, so those modules are evaluated BEFORE the body of
 * `stories.ts` finishes. A story file calling `story()` imported from
 * `stories.ts` therefore ran while `STORY_SOURCE` was still uninitialised:
 *
 *     ReferenceError: Cannot access 'STORY_SOURCE' before initialization
 *
 * — at prerender, not at typecheck, which is the worst place to find it. The
 * builder and everything it reads live here instead, so the dependency runs one
 * way: a story file imports this, `stories.ts` imports this, and neither
 * imports the other at runtime. The type import below is `import type`, erased
 * by the compiler, so it adds no runtime edge.
 *
 * `content/reports.ts` never hit this because a per-file report is a plain
 * object literal and calls nothing back.
 */
import type { Photo } from '@/content/sections'
import type { Story, StorySection } from '@/content/stories'


/** Where the words for these pages still live: the old site's own story page. */
export const STORY_SOURCE = 'https://digitopia.co/customers'

/**
 * Where the story PDFs live: the `website-documents` bucket, not `public/`.
 *
 * Same reasoning as `content/reports.ts` and `content/ebooks.ts` — large
 * documents that change on marketing's schedule, and putting them in the
 * repository would put every future revision in the git history forever.
 */
const BUCKET
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/success-story'

/**
 * One PDF's URL, from the object key exactly as the bucket holds it.
 *
 * Every one of these keys carries spaces — "18d08baa-…-Yazaki and
 * Digitopia.pdf" — so the encoding happens here, once, rather than being
 * hand-written into sixteen string literals where a single missed `%20` is
 * invisible until somebody clicks.
 */
export function pdf(objectKey: string): string {
  return `${BUCKET}/${objectKey.replace(/ /g, '%20')}`
}

/**
 * What is still missing for a story, said plainly and in one place.
 *
 * Deliberately the same sentence for all twenty-two: it is one gap with one
 * owner, not twenty-two separate judgements, and writing it once means filling
 * it in is a single search. The address it names is the page to copy the words
 * out of.
 *
 * **Sixteen of them now carry the story as a PDF** (supplied 2026-08-27), and
 * the check stays open for those too, in a shorter form: a document behind a
 * button is not the same thing as a page that says what changed, and a reader
 * who does not download still sees only the index's lede. What the PDF closes
 * is "there is nowhere to read this", not "this page has words of its own".
 */
function stillToCome(slug: string, name: string, doc: boolean, extra?: string): string {
  const source = doc
    ? 'The full story is now on the page as a PDF, so the words exist — what is missing is them '
      + 'being on the page, in the page’s own voice and readable without a download.'
    : `digitopia.co tells it at ${STORY_SOURCE}/${slug}/, and it was never imported, because the `
      + 'Customers page carried only the links and not the twenty-two pages behind them.'
  return (
    `Still to be supplied for this page: ${name}’s success story itself — what changed, what `
    + `was measured and what it made possible. ${source} The paragraphs above are the index `
    + `page’s own lede, which is true of all of them and specific to none.${extra ? ` ${extra}` : ''}`
  )
}

/**
 * The page's own lede, carried onto every story that has no words of its own.
 *
 * Verbatim from the Customers page — "Great Companies Work with Digitopia / See
 * how pioneers succeed with their transformation." — so nothing on these pages
 * is a claim this site is making up about a named customer. The first line is
 * the only company-specific sentence, and it says exactly what the old page's
 * own link said: that this company has a story here.
 */
function ledeFor(name: string): string[] {
  return [
    `${name} is one of the companies whose transformation Digitopia has been part of.`,
    'See how pioneers succeed with their transformation.',
  ]
}

export function story(
  slug: string,
  name: string,
  opts: {
    doc?: string
    video?: string
    extra?: string
    /** This page's own H1, where the reference heads it with more than the name. */
    headline?: string
    /** This story's own opening, where it has been written. Replaces `ledeFor`. */
    lede?: string[]
    intro?: string | string[]
    sections?: StorySection[]
    photo?: Photo
    heroPhoto?: Photo
    /** This page's own closing quote, where it differs from the Hall of Fame's. */
    closingQuote?: string[]
    /**
     * What is still missing on a page that *has* been written.
     *
     * An override rather than a flag, because `stillToCome` says the one thing
     * that is true of the twenty-one — "there are no words here" — and that is
     * exactly what is no longer true of the one. A written page still has a
     * gap; it is a different gap, and it has to be able to say so in its own
     * sentence. Omitting it entirely is not offered: a story assembled from a
     * screenshot is not the same as one signed off.
     */
    check?: string
    /** Not published. See `content/drafts.ts`. */
    draft?: boolean
  } = {},
): Story {
  return {
    slug,
    name,
    headline: opts.headline,
    body: opts.lede ?? ledeFor(name),
    intro: opts.intro,
    sections: opts.sections,
    photo: opts.photo,
    heroPhoto: opts.heroPhoto,
    closingQuote: opts.closingQuote,
    download: opts.doc ? pdf(opts.doc) : undefined,
    video: opts.video,
    check: opts.check ?? stillToCome(slug, name, Boolean(opts.doc), opts.extra),
    draft: opts.draft,
  }
}
